/**
 * Story 36.3 — the context attached to a "Send feedback" submission (ruling
 * R5). A pure read of the current location: no hook, no global state, no
 * URL normalisation (`useChatUrlState` rewrites params, so it is NOT used).
 *
 * The context is an allow-list, exactly
 * `{ route, task_id?, session_id?, build_id, viewport }`:
 * - `route` is the pathname only — never the query string or the hash.
 * - `task_id`: `?taskId` on `/chat` or `/tasks`, or the `/tasks/:taskId/review`
 *   path segment.
 * - `session_id`: `?advisoryId` on `/chat`, only when there is no task id
 *   (taskId wins, the `useChatUrlState` rule).
 * - Both ids must be valid UUIDs; a malformed one is treated as absent, and an
 *   absent optional key is omitted (the server schema refuses `null` there).
 * - `build_id` is `null`: the SPA exposes no build stamp to the client.
 * - `viewport` is `desktop` at ≥1024px, else `mobile`.
 *
 * A sibling module (not inside the dialog) because of
 * `react-refresh/only-export-components`.
 */

import { isValidUuid } from '@/lib/utils/uuid-regex';
import type { AppFeedbackContext } from '@/lib/api/feedback';

export interface FeedbackLocation {
  pathname: string;
  search: string;
}

const REVIEW_PATH = /^\/tasks\/([^/]+)\/review\/?$/;

export function buildAppFeedbackContext(
  location: FeedbackLocation,
  isDesktop: boolean,
): AppFeedbackContext {
  const { pathname } = location;
  const params = new URLSearchParams(location.search);
  const isChat = pathname === '/chat';
  const isTasks = pathname === '/tasks';

  let taskId: string | null = null;
  const reviewMatch = REVIEW_PATH.exec(pathname);
  if (reviewMatch) {
    taskId = reviewMatch[1];
  } else if (isChat || isTasks) {
    taskId = params.get('taskId');
  }
  if (!isValidUuid(taskId)) taskId = null;

  let sessionId: string | null = null;
  if (isChat && taskId === null) {
    const advisoryId = params.get('advisoryId');
    if (isValidUuid(advisoryId)) sessionId = advisoryId;
  }

  return {
    route: pathname,
    ...(taskId ? { task_id: taskId } : {}),
    ...(sessionId ? { session_id: sessionId } : {}),
    build_id: null,
    viewport: isDesktop ? 'desktop' : 'mobile',
  };
}

/** The "what is attached" line shown in the sheet. */
export function describeAttachedContext(context: AppFeedbackContext): string {
  if (context.task_id) return "the page you're on and this task";
  if (context.session_id) return "the page you're on and this chat";
  return "the page you're on";
}
